import { ResultsCollection } from '@sonar-software/xuder';
import { UseAsyncCollectionStateWithPreloadResult } from '@hooks/useAsyncCollectionStateWithPreload';
import { useAsyncState } from '@hooks/useAsyncState';
import { UseAsyncStateWithPreloadResult } from '@hooks/useAsyncStateWithPreload';
import { useOnMount } from '@hooks/useOnMount';
import { usePrevious } from '@hooks/usePrevious';

import { ActionFactory, useAction } from '@hooks/useAction';
import { AsyncStateSelector, createAsyncSelectors } from './selectors';
import { useEffect, useRef } from 'react';

export interface AsyncStatePreloadHookOptions<TData, TArgs, TOutput> {
	defaultRequest?: TArgs,
	loadAction: ActionFactory<TArgs>,
	selector: AsyncStateSelector<TData>,
	useTransform: (result: UseAsyncStateWithPreloadResult<TData, TArgs>) => TOutput
}

export function createAsyncStateWithPreloadHook<TData, TArgs, TOutput>(
	options: AsyncStatePreloadHookOptions<TData, TArgs, TOutput>
) {
	const selectors = createAsyncSelectors(options.selector);
	const { defaultRequest, loadAction } = options;

	const useAsyncStateWithPreloadInternal = function(
		refresh: boolean = false,
		initialArgs?: TArgs
	): TOutput {
		const loadingLock = useRef(false);
		const { data, isReceived, isRequested } = useAsyncState(selectors);
		const previousIsRequested = usePrevious(isRequested, isRequested);

		const load = useAction(loadAction);

		const shouldLoad = !(isRequested || isReceived) || refresh;

		useOnMount(() => {
			if (shouldLoad && !loadingLock.current) {
				loadingLock.current = true;
				load(initialArgs || defaultRequest);
			}
		});

		useEffect(() => {
			if (!isRequested && previousIsRequested) {
				loadingLock.current = false;
			}
		}, [ isRequested, previousIsRequested ]);

		return options.useTransform({
			data,
			isReceived,
			isRequested,
			request: load
		});
	};

	useAsyncStateWithPreloadInternal.defaultRequest = defaultRequest;
	useAsyncStateWithPreloadInternal.selectors = selectors;
	useAsyncStateWithPreloadInternal.loadAction = loadAction;

	return useAsyncStateWithPreloadInternal;
}

export interface AsyncCollectionStatePreloadHookOptions<TData, TArgs, TOutput> {
	defaultRequest?: TArgs,
	loadAction: ActionFactory<TArgs>,
	selector: AsyncStateSelector<ResultsCollection<TData>>,
	useTransform: (result: UseAsyncCollectionStateWithPreloadResult<TData, TArgs>) => TOutput
}

export function createAsyncCollectionStateWithPreloadHook<TData, TArgs, TOutput>(
	options: AsyncCollectionStatePreloadHookOptions<TData, TArgs, TOutput>
) {
	return createAsyncStateWithPreloadHook({
		...options,
		useTransform: (result) => options.useTransform({
			...result,
			data: result.data?.results
		})
	});
}
